/**
 * LRU Memoize is a function that memoizes a function by storing the results of the function in a cache.
 * It uses a Least Recently Used (LRU) cache to store the results.
 * It is a simple implementation of the LRU cache.
 * It is used to memoize functions that are called with the same arguments. Note: It only does referential checks,
 * So call with inline object like ({x: 1}) will not give cached result
 * It is used to improve the performance of the function.
 * It can be used in place of memoize one where we want to cache call with different arguments
 */
export function lruMemoize(fn, maxSize = 10) {
    const cache = new Map();
    const tokens = new WeakMap();
    let counter = 0;
    const getCompositeKey = (args) => {
        return args
            .map((arg) => {
            if (arg === null || arg === undefined) {
                return '';
            }
            else if (typeof arg === 'object' || typeof arg === 'function') {
                if (!tokens.has(arg)) {
                    tokens.set(arg, counter++);
                }
                return `obj:${tokens.get(arg)}`;
            }
            else {
                return `prim:${String(arg)}`;
            }
        })
            .join('|');
    };
    return ((...args) => {
        const key = getCompositeKey(args);
        if (cache.has(key)) {
            const cacheValue = cache.get(key);
            // if cache is present, delete it from top and move to end, so last latest used cache is not removed
            cache.delete(key);
            cache.set(key, cacheValue);
            return cacheValue;
        }
        const result = fn(...args);
        // set the result in cache
        cache.set(key, result);
        // if the cache is full, remove the oldest cache
        if (cache.size > maxSize) {
            const oldestEntry = cache.keys().next();
            if (!oldestEntry.done) {
                cache.delete(oldestEntry.value);
            }
        }
        return result;
    });
}
